import { useState } from "react";
import { updateAWDConfig } from "services/api";

export default function AWDConfigPanel({ mac, awdConfig, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});

  const startEditing = () => {
    setForm({
      dry_threshold_cm: awdConfig?.dry_threshold_cm ?? -15,
      flood_target_cm: awdConfig?.flood_target_cm ?? 5,
      max_dry_days: awdConfig?.max_dry_days ?? 7,
      min_dry_depth_cm: awdConfig?.min_dry_depth_cm ?? -3,
    });
    setError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateAWDConfig(mac, form);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, hint) => (
    <label className="block">
      <span className="text-xs text-gray-500">{label}</span>
      {hint && <span className="text-xs text-gray-400 ml-1">({hint})</span>}
      <input
        type="number"
        step="0.1"
        className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
        value={form[key] ?? ""}
        onChange={e => setForm(f => ({ ...f, [key]: parseFloat(e.target.value) }))}
      />
    </label>
  );

  if (!editing) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">AWD Thresholds</h3>
          <button className="text-sm text-blue-600 hover:underline" onClick={startEditing}>
            Edit
          </button>
        </div>
        {awdConfig ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600">
            <div><span className="text-gray-400">Dry threshold:</span> {awdConfig.dry_threshold_cm} cm</div>
            <div><span className="text-gray-400">Flood target:</span> +{awdConfig.flood_target_cm} cm</div>
            <div><span className="text-gray-400">Max dry days:</span> {awdConfig.max_dry_days} days</div>
            <div><span className="text-gray-400">Min dry depth:</span> {awdConfig.min_dry_depth_cm} cm</div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Loading…</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-700 mb-3">AWD Thresholds</h3>
      <div className="grid grid-cols-2 gap-3">
        {field("dry_threshold_cm", "Dry threshold (cm)", "e.g. -15")}
        {field("flood_target_cm", "Flood target (cm)", "e.g. 5")}
        {field("max_dry_days", "Max dry days", "alert if exceeded")}
        {field("min_dry_depth_cm", "Min dry depth (cm)", "to start a cycle")}
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={() => setEditing(false)} className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800">
          Cancel
        </button>
      </div>
    </div>
  );
}
